'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from 'next/image';
import { CatalogueLink } from '@/components/catalogue-navigation';
import { createPortal } from 'react-dom';
import {
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { usePathname } from 'next/navigation';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { MdKeyboardArrowRight, MdSort } from 'react-icons/md';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useHeaderCategoryChildren } from './use-header-category-children';

const CATEGORY_HOVER_INTENT_MS = 150;

function shouldCloseCatalogue(event: ReactMouseEvent<HTMLAnchorElement>): boolean {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    (event.currentTarget.target && event.currentTarget.target !== '_self') ||
    event.currentTarget.hasAttribute('download')
  ) {
    return false;
  }

  try {
    const target = new URL(event.currentTarget.href, window.location.href);
    const current = new URL(window.location.href);
    if (target.origin !== current.origin) return false;

    // Let core/native same-page anchor behavior run without changing the menu.
    return !(
      target.hash &&
      target.pathname === current.pathname &&
      target.search === current.search
    );
  } catch {
    return false;
  }
}

function hasCategoryChildren(category?: CategoriesEntity): boolean {
  if (!category) return false;
  if (typeof category.has_childs === 'boolean') return category.has_childs;
  if (Array.isArray(category.childs)) return category.childs.length > 0;

  // If an older API omits both fields, a harmless lazy request is safer than
  // permanently hiding real children from the navigation.
  return true;
}

export function HeaderCatalogue({
  categories,
  locale,
}: {
  categories: CategoriesEntity[];
  locale: string;
}) {
  const { isOpen, toggle, close } = useDisclosure();
  const [hoveredIndex, setHoveredIndex] = useState(0);
  const [mounted, setMounted] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hoverIntentRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pathname = usePathname();

  const { t } = useTranslations();

  const hoveredCategory = categories[hoveredIndex];
  const localChildren = hoveredCategory?.childs ?? [];
  const hasChildren = hasCategoryChildren(hoveredCategory);
  const childrenQuery = useHeaderCategoryChildren({
    locale,
    parentId: hoveredCategory?.id,
    enabled: isOpen && hasChildren && localChildren.length === 0,
  });
  const children = localChildren.length > 0 ? localChildren : (childrenQuery.data ?? []);
  const isLoadingChildren = localChildren.length === 0 && childrenQuery.isPending;
  const hasChildrenError = localChildren.length === 0 && childrenQuery.isError;

  const clearHoverIntent = useCallback(() => {
    if (!hoverIntentRef.current) return;
    clearTimeout(hoverIntentRef.current);
    hoverIntentRef.current = null;
  }, []);

  const showCategory = useCallback(
    (index: number) => {
      clearHoverIntent();
      setHoveredIndex(index);
    },
    [clearHoverIntent],
  );

  const scheduleCategory = useCallback(
    (index: number) => {
      clearHoverIntent();
      hoverIntentRef.current = setTimeout(() => showCategory(index), CATEGORY_HOVER_INTENT_MS);
    },
    [clearHoverIntent, showCategory],
  );

  // Mount portal after hydration
  useEffect(() => {
    setMounted(true);
  }, []);

  const handleCategoryClick = useCallback(
    (event: ReactMouseEvent<HTMLAnchorElement>) => {
      if (!shouldCloseCatalogue(event)) return;
      clearHoverIntent();
      close();
    },
    [clearHoverIntent, close],
  );

  // Also close when navigation originates outside this catalogue.
  useEffect(() => {
    clearHoverIntent();
    close();
    setHoveredIndex(0);
  }, [categories, locale, pathname, clearHoverIntent, close]);

  useEffect(() => () => clearHoverIntent(), [clearHoverIntent]);

  // Close on click inside the header but outside button/dropdown
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!buttonRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
        close();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, close]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  return (
    <>
      {/* Full-page overlay rendered into document.body to escape the header stacking context */}
      {mounted &&
        createPortal(
          <div
            className={cn(
              'fixed inset-0 z-[39] bg-black/25 transition-all duration-300 ease-out',
              isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
            )}
            onClick={close}
          />,
          document.body,
        )}

      <Button
        ref={buttonRef}
        variant={'none' as any}
        size={'none' as any}
        className="gap-1 text-sm font-bold lg:text-base"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        onClick={() => {
          clearHoverIntent();
          toggle();
        }}
      >
        <MdSort size={20} className="-scale-x-[1] text-black" />
        {t('text3316')}
      </Button>

      <div
        ref={dropdownRef}
        className={cn(
          'custom-container',
          'absolute left-1/2 top-full z-10 mt-5 -translate-x-1/2',
          'ea-header-catalogue__dropdown-wrapper',
          'transition-all duration-300 ease-out',
          isOpen
            ? 'pointer-events-auto translate-y-0 opacity-100'
            : 'pointer-events-none -translate-y-3 opacity-0',
        )}
      >
        <div className="grid w-full max-w-[1044px] grid-cols-2 gap-4 rounded-[20px] bg-white p-6">
          {/* Parent categories */}
          <div className="col-span-1 flex w-full flex-col items-start justify-start gap-1 rounded-[20px] bg-neutral-100 p-3">
            {categories.map((category, index) => (
              <CatalogueLink
                key={category.id}
                href={ROUTES.CATEGORY({ slug: category.slug })}
                prefetch={false}
                prefetchOnHover={isOpen}
                onClick={handleCategoryClick}
                onMouseEnter={() => scheduleCategory(index)}
                onFocus={() => showCategory(index)}
                className={cn(
                  'group',
                  'flex h-12 w-full items-center justify-between gap-[10px] rounded-[20px] px-3 transition-all duration-200',
                  hoveredIndex === index ? 'bg-white' : 'bg-transparent hover:bg-white',
                )}
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center">
                  <Image
                    src={category?.icon?.url ?? '/images/placeholder.png'}
                    alt={resolveImageAlt(category?.icon, category?.name)}
                    width={24}
                    height={24}
                    className="max-h-6 min-h-6 min-w-6 max-w-6 object-contain"
                  />
                </span>
                <Heading
                  tag="h6"
                  title={category.name}
                  className={{ container: 'w-full flex-1' }}
                  color="black"
                />
                {hasCategoryChildren(category) && (
                  <MdKeyboardArrowRight
                    className={cn(
                      'max-h-5 min-h-5 min-w-5 max-w-5 shrink-0 transition-all duration-200',
                      hoveredIndex === index
                        ? 'translate-x-0.5 text-primary'
                        : 'text-[#D1D5DB] group-hover:translate-x-0.5 group-hover:text-primary',
                    )}
                  />
                )}
              </CatalogueLink>
            ))}
          </div>

          {/* Children of hovered category */}
          <div className="col-span-1 flex w-full flex-col items-start justify-start rounded-[20px] border border-[#E5E7EB] p-3">
            {hasChildren && isLoadingChildren && (
              <div
                className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground"
                role="status"
              >
                <AiOutlineLoading3Quarters className="h-4 w-4 shrink-0 animate-spin" />
                Načítavam podkategórie…
              </div>
            )}
            {hasChildren && hasChildrenError && (
              <button
                type="button"
                className="px-3 py-4 text-left text-sm font-semibold text-primary"
                onClick={() => void childrenQuery.refetch()}
              >
                Podkategórie sa nepodarilo načítať. Skúsiť znova
              </button>
            )}
            {hasChildren && !isLoadingChildren && !hasChildrenError && (
              <div key={hoveredIndex} className="flex w-full animate-fadeIn flex-col">
                {children.map(child => (
                  <CatalogueLink
                    key={child.id}
                    href={ROUTES.CATEGORY({ slug: child.slug })}
                    onClick={handleCategoryClick}
                    prefetch={false}
                    prefetchOnHover={isOpen}
                    className="group flex h-12 w-full items-center justify-between gap-[10px] rounded-[20px] px-3 transition-all duration-200 hover:bg-[#F3F4F6]"
                  >
                    <Heading
                      tag="h6"
                      title={child.name}
                      className={{ container: 'w-full flex-1' }}
                      color="black"
                    />
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                      {hasCategoryChildren(child) && (
                        <MdKeyboardArrowRight className="h-5 w-5 text-[#D1D5DB] transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-primary" />
                      )}
                    </span>
                  </CatalogueLink>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
